/**
 * Custom Patch Order Form Logic
 *
 * Implements:
 * - TICK-1990: Quantity after patch selection (progressive disclosure)
 * - TICK-1991: Backing type selection for all patch types
 * - TICK-1992: Rubber patch mold fee ($140 for new orders)
 * - TICK-XXXX: Leather/cork per-size pricing with volume discounts
 *               - Pricing by (W+H)/2 average dimension (linear-inch model)
 *               - Mirrors Ninja Patches' published curve in CAD at 1.37 FX
 *               - 4" max dimension, 10pc minimum, no size surcharge
 *               - Sub-1.5" sizes priced at the 1.5" floor
 */
 
(function() {
  'use strict';
 
  // ============================================
  // LEATHER PRICING MATRIX
  // Per-size, per-quantity-tier pricing in CAD cents.
  // Built from Ninja Patches' published USD prices x 1.37 FX.
  // Rows = average dimension (W+H)/2 in inches, rounded to nearest 0.5
  // Columns = volume tier index (matches LEATHER_TIER_DEFS below)
  // ============================================
  const LEATHER_PRICING = {
    // [avg_size_inches]: [10-24, 25-49, 50-99, 100-199, 200-499, 500+] in CAD cents
    1.5: [567, 425, 369, 284, 199, 142],
    2.0: [615, 461, 400, 308, 215, 154],
    2.5: [669, 501, 435, 334, 234, 167],
    3.0: [722, 541, 469, 361, 253, 180],
    3.5: [823, 618, 535, 412, 288, 206],
    4.0: [927, 696, 603, 464, 325, 232]
  };
 
  // Leather volume tier definitions (used to build the tier-card UI and
  // to pick the right column from LEATHER_PRICING for a given quantity).
  const LEATHER_TIER_DEFS = [
    { index: 0, minQty: 10,  maxQty: 24,       label: '10-24',   discountLabel: null      },
    { index: 1, minQty: 25,  maxQty: 49,       label: '25-49',   discountLabel: '25% off' },
    { index: 2, minQty: 50,  maxQty: 99,       label: '50-99',   discountLabel: '35% off' },
    { index: 3, minQty: 100, maxQty: 199,      label: '100-199', discountLabel: '50% off' },
    { index: 4, minQty: 200, maxQty: 499,      label: '200-499', discountLabel: '65% off' },
    { index: 5, minQty: 500, maxQty: Infinity, label: '500+',    discountLabel: '75% off' }
  ];
 
  // Leather/cork-specific config
  const LEATHER_MIN_QTY = 10;
  const LEATHER_MAX_DIMENSION = 4;     // inches
  const LEATHER_MIN_PRICED_SIZE = 1.5; // anything smaller is priced at this floor
  const LEATHER_MAX_PRICED_SIZE = 4.0;
 
  // ============================================
  // PRICING TIERS BY PATCH TYPE
  // Prices in cents, matched to Shopify automatic discounts
  // (Leather is handled separately via LEATHER_PRICING above; the entry
  // here is kept for the tier-card UI structure and lead time only.)
  // ============================================
  const PRICING_TIERS = {
    leather: {
      minQty: LEATHER_MIN_QTY,
      leadTime: '2-3 business days',
      // Leather pricing is per-size; the tiers array here is rebuilt
      // dynamically from LEATHER_PRICING when a size is entered.
      // This placeholder uses the 2.0" row so the UI has something to
      // show before a size is entered.
      tiers: buildLeatherTiersForSize(2.0)
    },
    fabric: {
      minQty: 10,
      leadTime: '2-3 business days',
      tiers: [
        { minQty: 10, maxQty: 24, priceCents: 700, label: '10-24' },
        { minQty: 25, maxQty: 49, priceCents: 600, label: '25-49', savings: '$1.00 off' },
        { minQty: 50, maxQty: 99, priceCents: 500, label: '50-99', savings: '$2.00 off' },
        { minQty: 100, maxQty: 249, priceCents: 400, label: '100-249', savings: '$3.00 off' },
        { minQty: 250, maxQty: Infinity, priceCents: 350, label: '250+', savings: '$3.50 off' }
      ]
    },
    rubber: {
      minQty: 25,
      leadTime: '4 weeks',
      tiers: [
        { minQty: 25, maxQty: 49, priceCents: 1200, label: '25-49' },
        { minQty: 50, maxQty: 99, priceCents: 1000, label: '50-99', savings: '$2.00 off' },
        { minQty: 100, maxQty: 249, priceCents: 700, label: '100-249', savings: '$5.00 off' },
        { minQty: 250, maxQty: Infinity, priceCents: 600, label: '250+', savings: '$6.00 off' }
      ]
    },
    embroidery: {
      minQty: 25,
      leadTime: '2-3 weeks',
      tiers: [
        { minQty: 25, maxQty: 49, priceCents: 600, label: '25-49' },
        { minQty: 50, maxQty: 99, priceCents: 510, label: '50-99', savings: '$0.90 off' },
        { minQty: 100, maxQty: 249, priceCents: 425, label: '100-249', savings: '$1.75 off' },
        { minQty: 250, maxQty: Infinity, priceCents: 336, label: '250+', savings: '$2.64 off' }
      ]
    }
  };
 
  // ============================================
  // LEATHER PRICING HELPERS
  // ============================================
 
  // Get the average dimension to use for pricing lookup.
  // Uses (W+H)/2 rounded to nearest 0.5", floored at 1.5" and capped at 4.0".
  function getLeatherPricingSize(width, height) {
    if (!(width > 0) || !(height > 0)) return null;
    const avg = (width + height) / 2;
    let rounded = Math.round(avg * 2) / 2; // snap to nearest 0.5"
    if (rounded < LEATHER_MIN_PRICED_SIZE) rounded = LEATHER_MIN_PRICED_SIZE;
    if (rounded > LEATHER_MAX_PRICED_SIZE) rounded = LEATHER_MAX_PRICED_SIZE;
    return rounded;
  }
 
  // Look up the leather price for a given pricing-size and tier index.
  function getLeatherPriceCents(pricingSize, tierIndex) {
    const row = LEATHER_PRICING[pricingSize];
    if (!row) return null;
    return row[tierIndex];
  }
 
  // Build the leather tier array (in the same shape as PRICING_TIERS uses)
  // for a specific pricing size. This populates the per-quantity tier cards.
  function buildLeatherTiersForSize(pricingSize) {
    const row = LEATHER_PRICING[pricingSize] || LEATHER_PRICING[2.0];
    return LEATHER_TIER_DEFS.map(def => ({
      minQty: def.minQty,
      maxQty: def.maxQty,
      priceCents: row[def.index],
      label: def.label,
      savings: def.discountLabel
    }));
  }
 
  // Refresh the leather pricing tiers in PRICING_TIERS based on current size.
  function refreshLeatherPricing() {
    const pricingSize = getLeatherPricingSize(state.patchWidth, state.patchHeight);
    state.leatherPricingSize = pricingSize;
    if (pricingSize) {
      PRICING_TIERS.leather.tiers = buildLeatherTiersForSize(pricingSize);
    }
  }
 
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  const state = {
    // Patch selection
    selectedPatchType: null,      // 'leather', 'embroidery', 'fabric', 'rubber'
    selectedBackingType: null,    // 'normal', 'heat-activated'
    selectedQuantity: 1,
 
    // Patch dimensions
    patchWidth: null,             // Width in inches
    patchHeight: null,            // Height in inches
    squareInches: null,           // Calculated area
    sizeSurchargeApplies: false,  // True if > threshold sq inches (non-leather only)
 
    // Leather pricing (size-based)
    leatherPricingSize: null,     // The (W+H)/2 size rounded to nearest 0.5", floored/capped
 
    // Patch-specific config
    patchShape: null,
    patchLocation: null,
    patchMaterial: null,
    patchMaterialTitle: null,
    patchMaterialVariantId: null,
 
    // Embroidery-specific
    embroiderySize: null,
 
    // Rubber-specific (TICK-1992)
    isRubberReorder: null,
 
    // Logo
    logoFile: null,
    logoBase64: null,
 
    // Pricing data
    rubberMoldFeeCents: 14000,    // $140
 
    // TICK-2029: Tiered size surcharges (does NOT apply to leather)
    sizeSurchargeTiers: [
      { minSqIn: 9,  maxSqIn: 16,       surchargeCents: 100, label: '9-16 sq in' },
      { minSqIn: 16, maxSqIn: 25,       surchargeCents: 200, label: '16-25 sq in' },
      { minSqIn: 25, maxSqIn: 36,       surchargeCents: 300, label: '25-36 sq in' },
      { minSqIn: 36, maxSqIn: 49,       surchargeCents: 400, label: '36-49 sq in' },
      { minSqIn: 49, maxSqIn: Infinity, surchargeCents: 500, label: '49+ sq in' }
    ],
    currentSizeSurchargeCents: 0,
 
    // Product/variant IDs
    selectedVariantId: null
  };
 
  // ============================================
  // INITIALIZATION
  // ============================================
  function init() {
    loadPricingTiers();
    setupPatchTypeSelection();
    setupBackingTypeSelection();
    setupDimensionInputs();
    setupPresetSizes(); // TICK-2031: Fabric preset sizes
    setupPatchConfiguration();
    setupQuantitySelection();
    setupLogoUpload();
    setupOrderButton();
    setupNotificationSystem();
    updateUI();
  }
 
  // ============================================
  // LOAD SETTINGS FROM DOM
  // ============================================
  function loadPricingTiers() {
    const form = document.querySelector('[data-patch-form]');
    if (!form) return;
 
    state.rubberMoldFeeCents = parseInt(form.dataset.moldFeeCents) || 14000;
 
    // Load size surcharge settings
    state.sizeSurchargeThreshold = parseFloat(form.dataset.sizeSurchargeThreshold) || 9;
    const surchargePrice = form.dataset.sizeSurchargePrice;
    if (surchargePrice) {
      state.sizeSurchargeCents = Math.round(parseFloat(surchargePrice) * 100);
    }
  }
 
  // ============================================
  // GET PRICING FOR PATCH TYPE
  // ============================================
  function getPricingForType(patchType) {
    return PRICING_TIERS[patchType] || PRICING_TIERS.leather;
  }
 
  function getMinQuantityForType(patchType) {
    const pricing = getPricingForType(patchType);
    return pricing.minQty;
  }
 
  // Patch types that get the size surcharge (leather is excluded since
  // its price is already size-based).
  function patchTypeUsesSizeSurcharge(patchType) {
    return patchType !== 'leather';
  }
 
  // Maximum allowed dimension for a given patch type.
  function getMaxDimensionForType(patchType) {
    return patchType === 'leather' ? LEATHER_MAX_DIMENSION : 7;
  }
 
  // ============================================
  // PATCH DIMENSIONS
  // ============================================
  function setupDimensionInputs() {
    const widthInput = document.querySelector('[data-patch-width]');
    const heightInput = document.querySelector('[data-patch-height]');
 
    if (widthInput) {
      widthInput.addEventListener('input', handleDimensionChange);
      widthInput.addEventListener('change', handleDimensionChange);
    }
 
    if (heightInput) {
      heightInput.addEventListener('input', handleDimensionChange);
      heightInput.addEventListener('change', handleDimensionChange);
    }
  }
 
  function handleDimensionChange() {
    const widthInput = document.querySelector('[data-patch-width]');
    const heightInput = document.querySelector('[data-patch-height]');
    const sqInDisplay = document.querySelector('[data-square-inches]');
    const surchargeNotice = document.querySelector('[data-size-surcharge-notice]');
    const surchargeText = document.querySelector('[data-surcharge-text]');
 
    let width = parseFloat(widthInput?.value) || 0;
    let height = parseFloat(heightInput?.value) || 0;
 
    // Enforce max size — varies by patch type (leather=4", others=7")
    const maxSize = getMaxDimensionForType(state.selectedPatchType);
    if (width > maxSize) {
      width = maxSize;
      widthInput.value = maxSize;
      showNotification(`Maximum patch width is ${maxSize} inches.`, 'error');
    }
    if (height > maxSize) {
      height = maxSize;
      heightInput.value = maxSize;
      showNotification(`Maximum patch height is ${maxSize} inches.`, 'error');
    }
 
    state.patchWidth = width > 0 ? width : null;
    state.patchHeight = height > 0 ? height : null;
 
    if (width > 0 && height > 0) {
      state.squareInches = width * height;
 
      // Size surcharge — only for non-leather patch types
      if (patchTypeUsesSizeSurcharge(state.selectedPatchType)) {
        const surchargeTier = getSurchargeTierForSize(state.squareInches);
        state.sizeSurchargeApplies = surchargeTier !== null;
        state.currentSizeSurchargeCents = surchargeTier ? surchargeTier.surchargeCents : 0;
      } else {
        state.sizeSurchargeApplies = false;
        state.currentSizeSurchargeCents = 0;
      }
 
      // Update area display
      if (sqInDisplay) {
        sqInDisplay.textContent = state.squareInches.toFixed(2) + ' sq in';
        if (state.sizeSurchargeApplies) {
          sqInDisplay.style.background = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
          sqInDisplay.style.color = '#ffffff';
        } else {
          sqInDisplay.style.background = 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
          sqInDisplay.style.color = '#ffffff';
        }
      }
 
      // Show/hide surcharge notice (non-leather only)
      if (surchargeNotice) {
        if (state.sizeSurchargeApplies) {
          const surchargeTier = getSurchargeTierForSize(state.squareInches);
          surchargeNotice.style.display = 'flex';
          if (surchargeText && surchargeTier) {
            const surchargeAmount = (surchargeTier.surchargeCents / 100).toFixed(2);
            surchargeText.textContent = `+$${surchargeAmount}/patch for ${surchargeTier.label}`;
          }
        } else {
          surchargeNotice.style.display = 'none';
        }
      }
 
      // Leather: refresh size-based pricing and rebuild tier cards
      if (state.selectedPatchType === 'leather') {
        refreshLeatherPricing();
        updateTierCardsForPatchType('leather');
        // Re-highlight the tier matching the current quantity
        if (state.selectedQuantity > 0) {
          highlightTierForQuantity(state.selectedQuantity);
        }
      }
    } else {
      state.squareInches = null;
      state.sizeSurchargeApplies = false;
      state.currentSizeSurchargeCents = 0;
      state.leatherPricingSize = null;
 
      if (sqInDisplay) {
        sqInDisplay.textContent = '-- sq in';
        sqInDisplay.style.background = 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)';
        sqInDisplay.style.color = '#ffffff';
      }
 
      if (surchargeNotice) {
        surchargeNotice.style.display = 'none';
      }
    }
 
    // Update pricing if quantity is already selected
    if (state.selectedQuantity > 0) {
      updatePricing();
    }
 
    // Auto-select embroidery size based on dimensions
    if (state.selectedPatchType === 'embroidery' && state.patchWidth > 0 && state.patchHeight > 0) {
      autoSelectEmbroiderySize();
    }
 
    checkConfigComplete();
    updateUI();
  }
 
  // TICK-2029: Get surcharge tier for a given square inch size
  function getSurchargeTierForSize(sqIn) {
    if (!sqIn || sqIn <= 0) return null;
 
    for (const tier of state.sizeSurchargeTiers) {
      if (sqIn >= tier.minSqIn && sqIn <= tier.maxSqIn) {
        return tier;
      }
    }
    const lastTier = state.sizeSurchargeTiers[state.sizeSurchargeTiers.length - 1];
    if (sqIn >= lastTier.minSqIn) {
      return lastTier;
    }
    return null;
  }
 
  // Update the "Maximum patch size" hint text below the dimension inputs
  // to reflect the current patch type's limit.
  function updateMaxSizeHint() {
    const dimSection = document.querySelector('[data-dimensions-section]');
    if (!dimSection) return;
    const max = getMaxDimensionForType(state.selectedPatchType);
    // Find the small "Maximum patch size:" hint paragraph and update it.
    const hints = dimSection.querySelectorAll('p');
    hints.forEach(p => {
      if (p.textContent.includes('Maximum patch size')) {
        // Preserve the leading info icon by replacing only the text node content.
        const text = ` Maximum patch size: ${max}" × ${max}"`;
        // Walk child nodes and replace the trailing text run.
        let updated = false;
        p.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE && node.textContent.includes('Maximum patch size')) {
            node.textContent = text;
            updated = true;
          }
        });
        if (!updated) {
          // Fallback: just set the text content.
          p.textContent = text;
        }
      }
    });
 
    // Also update the input max attributes so the browser-level validation
    // matches the patch type. We keep step/min as the originals.
    const widthInput = document.querySelector('[data-patch-width]');
    const heightInput = document.querySelector('[data-patch-height]');
    if (widthInput) widthInput.max = max;
    if (heightInput) heightInput.max = max;
  }
 
  // ============================================
  // TICK-2031: FABRIC PRESET SIZES
  // ============================================
  function setupPresetSizes() {
    document.querySelectorAll('[data-preset-size]').forEach(btn => {
      btn.addEventListener('click', function() {
        const width = parseFloat(this.dataset.width);
        const height = parseFloat(this.dataset.height);
        const shape = this.dataset.shape;
 
        const widthInput = document.querySelector('[data-patch-width]');
        const heightInput = document.querySelector('[data-patch-height]');
 
        if (widthInput) widthInput.value = width;
        if (heightInput) heightInput.value = height;
 
        if (shape === 'circle' && state.selectedPatchType === 'fabric') {
          const circleBtn = document.querySelector('[data-patch-shape="circle"]');
          if (circleBtn) {
            circleBtn.click();
          }
        }
 
        handleDimensionChange();
 
        document.querySelectorAll('[data-preset-size]').forEach(b => {
          b.style.borderColor = '#bbf7d0';
          b.style.background = 'white';
          b.dataset.selected = '';
        });
        this.style.borderColor = '#22c55e';
        this.style.background = '#f0fdf4';
        this.dataset.selected = 'true';
      });
 
      btn.addEventListener('mouseenter', function() {
        if (this.dataset.selected !== 'true') {
          this.style.borderColor = '#22c55e';
          this.style.background = '#f0fdf4';
        }
      });
      btn.addEventListener('mouseleave', function() {
        if (this.dataset.selected !== 'true') {
          this.style.borderColor = '#bbf7d0';
          this.style.background = 'white';
        }
      });
    });
  }
 
  function showFabricPresetSizes(show) {
    const presetSection = document.querySelector('[data-fabric-preset-sizes]');
    if (presetSection) {
      presetSection.style.display = show ? 'block' : 'none';
    }
  }
 
  // TICK-2202: Reset dimension inputs and state (used when switching to fabric)
  function resetDimensionInputs() {
    const widthInput = document.querySelector('[data-patch-width]');
    const heightInput = document.querySelector('[data-patch-height]');
    if (widthInput) widthInput.value = '';
    if (heightInput) heightInput.value = '';
    state.patchWidth = null;
    state.patchHeight = null;
    state.squareInches = null;
    state.leatherPricingSize = null;
    const sqInDisplay = document.querySelector('[data-square-inches]');
    if (sqInDisplay) sqInDisplay.textContent = '-- sq in';
    document.querySelectorAll('[data-preset-size]').forEach(b => {
      b.style.borderColor = '#bbf7d0';
      b.style.background = 'white';
      b.dataset.selected = '';
    });
  }
 
  // ============================================
  // EMBROIDERY SIZE AUTO-SELECTION
  // ============================================
  function autoSelectEmbroiderySize() {
    const maxDimension = Math.max(state.patchWidth || 0, state.patchHeight || 0);
 
    let requiredSize = 'small';
    if (maxDimension > 3.5) {
      requiredSize = 'large';
    } else if (maxDimension > 2) {
      requiredSize = 'medium';
    }
 
    state.embroiderySize = requiredSize;
 
    document.querySelectorAll('[data-embroidery-size]').forEach(el => {
      el.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
 
      const size = el.dataset.embroiderySize;
 
      if ((size === 'small' && maxDimension > 2) ||
          (size === 'medium' && maxDimension > 3.5)) {
        el.style.opacity = '0.4';
        el.style.pointerEvents = 'none';
        el.style.cursor = 'not-allowed';
      } else {
        el.style.opacity = '1';
        el.style.pointerEvents = 'auto';
        el.style.cursor = 'pointer';
      }
 
      if (size === requiredSize) {
        el.classList.add('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
      }
    });
 
    const sizeLabels = { small: 'Small (up to 2")', medium: 'Medium (2" - 3.5")', large: 'Large (3.5"+)' };
    if (maxDimension > 0) {
      console.log(`Auto-selected embroidery size: ${sizeLabels[requiredSize]} based on ${maxDimension}" dimension`);
    }
  }
 
  // ============================================
  // STEP 1: PATCH TYPE SELECTION
  // ============================================
  function setupPatchTypeSelection() {
    const patchCards = document.querySelectorAll('[data-patch-type]');
 
    patchCards.forEach(card => {
      card.addEventListener('click', function() {
        const type = this.dataset.patchType;
        const minQty = parseInt(this.dataset.minQuantity) || 1;
 
        selectPatchType(type, this.dataset.variantId);
 
        patchCards.forEach(c => {
          c.classList.remove('ring-2', 'ring-primary', 'border-primary', 'bg-primary/5');
        });
        this.classList.add('ring-2', 'ring-primary', 'border-primary', 'bg-primary/5');
      });
    });
  }
 
  function selectPatchType(type, variantId) {
    state.selectedPatchType = type;
    state.selectedVariantId = variantId;
 
    // Reset dependent selections
    state.selectedBackingType = null;
    state.patchShape = null;
    state.patchLocation = null;
    state.patchMaterial = null;
    state.patchMaterialTitle = null;
    state.embroiderySize = null;
    state.isRubberReorder = null;
 
    // Reset quantity to minimum for this patch type
    const minQty = getMinQuantityForType(type);
    state.selectedQuantity = minQty;
 
    // Update the dimension input maxes and the "Maximum patch size" hint
    updateMaxSizeHint();
 
    // If switching INTO leather and dimensions are already entered, refresh
    // leather pricing. If switching OUT of leather, clear the leather state.
    if (type === 'leather') {
      refreshLeatherPricing();
    } else {
      state.leatherPricingSize = null;
    }
 
    // If a non-leather type is selected and existing dimensions exceed its
    // max, snap them down. (Only relevant when switching FROM leather TO
    // a type with a higher cap, which won't happen, but keeping symmetric.)
    const maxDim = getMaxDimensionForType(type);
    const widthInput = document.querySelector('[data-patch-width]');
    const heightInput = document.querySelector('[data-patch-height]');
    if (widthInput && parseFloat(widthInput.value) > maxDim) {
      widthInput.value = maxDim;
    }
    if (heightInput && parseFloat(heightInput.value) > maxDim) {
      heightInput.value = maxDim;
    }
    handleDimensionChange();
 
    // Update the tier cards display for this patch type
    updateTierCardsForPatchType(type);
 
    // TICK-2028: Update lead time display for this patch type
    updateLeadTimeDisplay(type);
 
    // Show backing section
    showSection('[data-backing-section]');
    scrollToSection('[data-backing-section]');
 
    // Hide config and quantity sections until backing is selected
    hideSection('[data-config-section]');
    hideSection('[data-quantity-section]');
    hideSection('[data-price-section]');
 
    // Reset backing selection UI
    document.querySelectorAll('[data-backing-type]').forEach(el => {
      el.classList.remove('ring-2', 'ring-green-500', 'border-green-500', 'bg-green-50');
    });
 
    updateUI();
  }
 
  // ============================================
  // TICK-2028: UPDATE LEAD TIME DISPLAY
  // ============================================
  function updateLeadTimeDisplay(patchType) {
    const leadTimeSpan = document.querySelector('[data-lead-time]');
    if (!leadTimeSpan) return;
 
    const pricing = getPricingForType(patchType);
    leadTimeSpan.textContent = pricing.leadTime || '2-3 business days';
  }
 
  // ============================================
  // UPDATE TIER CARDS FOR SELECTED PATCH TYPE
  // ============================================
  function updateTierCardsForPatchType(patchType) {
    const pricing = getPricingForType(patchType);
    const tiers = pricing.tiers;
    const tierContainer = document.querySelector('[data-tier-cards]');
 
    if (!tierContainer) return;
 
    tierContainer.innerHTML = '';
 
    // For leather: if no size has been entered yet, show a prompt instead of
    // potentially-misleading default prices.
    if (patchType === 'leather' && !state.leatherPricingSize) {
      const prompt = document.createElement('p');
      prompt.className = 'col-span-full text-center text-gray-500 py-4';
      prompt.textContent = 'Enter patch dimensions above to see pricing';
      tierContainer.appendChild(prompt);
 
      const quantityInput = document.querySelector('[data-quantity-input]');
      if (quantityInput) {
        quantityInput.min = pricing.minQty;
        if (parseInt(quantityInput.value, 10) < pricing.minQty) {
          quantityInput.value = pricing.minQty;
        }
      }
      return;
    }
 
    tiers.forEach((tier, index) => {
      const priceFormatted = (tier.priceCents / 100).toFixed(2);
      const isLast = index === tiers.length - 1;
      const clickQuantity = isLast ? tier.minQty : tier.maxQty;
 
      const card = document.createElement('button');
      card.setAttribute('data-tier-select', '');
      card.setAttribute('data-tier', index + 1);
      card.setAttribute('data-quantity', clickQuantity);
      card.className = 'p-4 border-2 border-gray-300 rounded-lg text-center hover:border-blue-500 hover:bg-blue-50 transition-all';
      card.style.backgroundColor = 'white';
 
      card.innerHTML = `
        <div class="text-2xl font-bold text-gray-900">${tier.label}</div>
        <div class="text-sm font-bold text-blue-600 mt-1">$${priceFormatted}/ea</div>
        ${tier.savings ? `<div class="text-xs text-green-600">${tier.savings}</div>` : ''}
      `;
 
      card.addEventListener('click', function() {
        selectQuantity(clickQuantity);
        updateQuantityInput(clickQuantity);
 
        document.querySelectorAll('[data-tier-select]').forEach(b => {
          b.classList.remove('border-blue-500', 'bg-blue-50', 'ring-2', 'ring-blue-500');
        });
        this.classList.add('border-blue-500', 'bg-blue-50', 'ring-2', 'ring-blue-500');
      });
 
      tierContainer.appendChild(card);
    });
 
    const quantityInput = document.querySelector('[data-quantity-input]');
    if (quantityInput) {
      quantityInput.min = pricing.minQty;
      if (parseInt(quantityInput.value, 10) < pricing.minQty) {
        quantityInput.value = pricing.minQty;
      }
    }
  }
 
  // ============================================
  // STEP 2: BACKING TYPE SELECTION (TICK-1991)
  // ============================================
  function setupBackingTypeSelection() {
    const backingCards = document.querySelectorAll('[data-backing-type]');
 
    backingCards.forEach(card => {
      card.addEventListener('click', function() {
        const type = this.dataset.backingType;
        selectBackingType(type);
 
        backingCards.forEach(c => {
          c.classList.remove('ring-2', 'ring-green-500', 'border-green-500', 'bg-green-50');
        });
        this.classList.add('ring-2', 'ring-green-500', 'border-green-500', 'bg-green-50');
      });
    });
  }
 
  function selectBackingType(type) {
    state.selectedBackingType = type;
 
    showConfigSection();
    showSection('[data-config-section]');
    scrollToSection('[data-config-section]');
 
    updateUI();
  }
 
  // ============================================
  // STEP 3: PATCH CONFIGURATION
  // ============================================
  function setupPatchConfiguration() {
    document.querySelectorAll('[data-patch-shape]').forEach(el => {
      el.addEventListener('click', function() {
        state.patchShape = this.dataset.patchShape;
        document.querySelectorAll('[data-patch-shape]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
        checkConfigComplete();
      });
    });
 
    document.querySelectorAll('[data-patch-location]').forEach(el => {
      el.addEventListener('click', function() {
        state.patchLocation = this.dataset.patchLocation;
        document.querySelectorAll('[data-patch-location]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
        checkConfigComplete();
      });
    });
 
    document.querySelectorAll('[data-material^="leather-"]').forEach(el => {
      el.addEventListener('click', function() {
        state.patchMaterial = this.dataset.material;
        state.patchMaterialTitle = this.dataset.materialTitle;
        state.patchMaterialVariantId = this.dataset.materialVariantId;
        document.querySelectorAll('[data-material^="leather-"]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500');
        checkConfigComplete();
      });
    });
 
    document.querySelectorAll('[data-material^="fabric-"]').forEach(el => {
      el.addEventListener('click', function() {
        state.patchMaterial = this.dataset.material;
        state.patchMaterialTitle = this.dataset.materialTitle;
        state.patchMaterialVariantId = this.dataset.materialVariantId;
        document.querySelectorAll('[data-material^="fabric-"]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500');
        checkConfigComplete();
      });
    });
 
    document.querySelectorAll('[data-embroidery-size]').forEach(el => {
      el.addEventListener('click', function() {
        const clickedSize = this.dataset.embroiderySize;
        const maxDimension = Math.max(state.patchWidth || 0, state.patchHeight || 0);
 
        if (maxDimension > 0) {
          if (clickedSize === 'small' && maxDimension > 2) {
            showNotification('Small size only supports patches up to 2". Your dimensions require Medium or Large.', 'error');
            return;
          }
          if (clickedSize === 'medium' && maxDimension > 3.5) {
            showNotification('Medium size only supports patches up to 3.5". Your dimensions require Large.', 'error');
            return;
          }
        }
 
        state.embroiderySize = clickedSize;
        document.querySelectorAll('[data-embroidery-size]').forEach(c => {
          c.classList.remove('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
        });
        this.classList.add('ring-2', 'ring-purple-500', 'border-purple-500', 'bg-purple-50');
        checkConfigComplete();
      });
    });
 
    document.querySelectorAll('[data-mold-reorder]').forEach(el => {
      el.addEventListener('click', function() {
        const isReorder = this.dataset.moldReorder === 'yes';
        state.isRubberReorder = isReorder;
 
        document.querySelectorAll('[data-mold-reorder]').forEach(c => {
          c.classList.remove('ring-2', 'ring-green-500', 'ring-orange-500', 'border-green-500', 'border-orange-500', 'bg-green-50', 'bg-orange-50');
        });
 
        if (isReorder) {
          this.classList.add('ring-2', 'ring-green-500', 'border-green-500', 'bg-green-50');
        } else {
          this.classList.add('ring-2', 'ring-orange-500', 'border-orange-500', 'bg-orange-50');
        }
 
        checkConfigComplete();
      });
    });
  }
 
  function showConfigSection() {
    hideSection('[data-leather-fabric-config]');
    hideSection('[data-embroidery-config]');
    hideSection('[data-rubber-config]');
    hideSection('[data-leather-materials]');
    hideSection('[data-fabric-materials]');
    showFabricPresetSizes(false);
    showSection('[data-dimensions-section]');
 
    const subtitle = document.querySelector('[data-config-subtitle]');
 
    switch (state.selectedPatchType) {
      case 'leather':
        showSection('[data-leather-fabric-config]');
        showSection('[data-leather-materials]');
        if (subtitle) subtitle.textContent = 'Select shape and material for your leather patch';
        break;
 
      case 'fabric':
        showSection('[data-leather-fabric-config]');
        showSection('[data-fabric-materials]');
        showFabricPresetSizes(true);
        hideSection('[data-dimensions-section]');
        resetDimensionInputs();
        if (subtitle) subtitle.textContent = 'Select size, shape and material for your fabric patch';
        break;
 
      case 'embroidery':
        showSection('[data-embroidery-config]');
        if (subtitle) subtitle.textContent = 'Enter dimensions for your embroidered patch';
        break;
 
      case 'rubber':
        showSection('[data-rubber-config]');
        if (subtitle) subtitle.textContent = 'Tell us about your rubber patch order';
        break;
    }
  }
 
  function checkConfigComplete() {
    let isComplete = false;
 
    const hasDimensions = state.patchWidth > 0 && state.patchHeight > 0;
 
    switch (state.selectedPatchType) {
      case 'leather':
      case 'fabric':
        isComplete = state.patchShape && state.patchMaterial && hasDimensions;
        break;
 
      case 'embroidery':
        isComplete = hasDimensions;
        break;
 
      case 'rubber':
        isComplete = state.isRubberReorder !== null && hasDimensions;
        break;
    }
 
    if (isComplete) {
      showSection('[data-quantity-section]');
      scrollToSection('[data-quantity-section]');
      updateUI();
    }
  }
 
  // ============================================
  // STEP 4: QUANTITY SELECTION (TICK-1990)
  // ============================================
  function setupQuantitySelection() {
    document.querySelectorAll('[data-tier-select]').forEach(btn => {
      btn.addEventListener('click', function() {
        const qty = parseInt(this.dataset.quantity) || 1;
        selectQuantity(qty);
 
        document.querySelectorAll('[data-tier-select]').forEach(b => {
          b.classList.remove('border-blue-500', 'bg-blue-50', 'ring-2', 'ring-blue-500');
        });
        this.classList.add('border-blue-500', 'bg-blue-50', 'ring-2', 'ring-blue-500');
      });
    });
 
    const quantityInput = document.querySelector('[data-quantity-input]');
    if (quantityInput) {
      quantityInput.addEventListener('change', function() {
        const minQty = state.selectedPatchType ? getMinQuantityForType(state.selectedPatchType) : 1;
        const qty = Math.max(minQty, parseInt(this.value) || minQty);
        this.value = qty;
        selectQuantity(qty);
        highlightTierForQuantity(qty);
      });
    }
 
    const decreaseBtn = document.querySelector('[data-quantity-decrease]');
    const increaseBtn = document.querySelector('[data-quantity-increase]');
 
    if (decreaseBtn) {
      decreaseBtn.addEventListener('click', () => {
        const minQty = state.selectedPatchType ? getMinQuantityForType(state.selectedPatchType) : 1;
        const newQty = Math.max(minQty, state.selectedQuantity - 1);
        selectQuantity(newQty);
        updateQuantityInput(newQty);
        highlightTierForQuantity(newQty);
      });
    }
 
    if (increaseBtn) {
      increaseBtn.addEventListener('click', () => {
        const newQty = state.selectedQuantity + 1;
        selectQuantity(newQty);
        updateQuantityInput(newQty);
        highlightTierForQuantity(newQty);
      });
    }
  }
 
  function selectQuantity(qty) {
    if (!state.selectedPatchType) return;
 
    const minQty = getMinQuantityForType(state.selectedPatchType);
    if (qty < minQty) {
      qty = minQty;
      updateQuantityInput(qty);
      showNotification(`Minimum quantity for this patch type is ${minQty}`, 'error');
    }
 
    state.selectedQuantity = qty;
 
    const minWarning = document.querySelector('[data-min-qty-warning]');
    if (minWarning) {
      if (qty < minQty) {
        minWarning.style.display = 'block';
        minWarning.querySelector('span').textContent = minQty;
      } else {
        minWarning.style.display = 'none';
      }
    }
 
    showSection('[data-price-section]');
    scrollToSection('[data-logo-section]');
    updatePricing();
    updateUI();
  }
 
  function updateQuantityInput(qty) {
    const input = document.querySelector('[data-quantity-input]');
    if (input) input.value = qty;
  }
 
  function highlightTierForQuantity(qty) {
    if (!state.selectedPatchType) return;
 
    document.querySelectorAll('[data-tier-select]').forEach(btn => {
      btn.classList.remove('border-blue-500', 'bg-blue-50', 'ring-2', 'ring-blue-500');
    });
 
    const pricing = getPricingForType(state.selectedPatchType);
    const tiers = pricing.tiers;
 
    let matchingTierIndex = 0;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) {
        matchingTierIndex = i + 1;
        break;
      }
    }
 
    const btn = document.querySelector(`[data-tier-select][data-tier="${matchingTierIndex}"]`);
    if (btn) {
      btn.classList.add('border-blue-500', 'bg-blue-50', 'ring-2', 'ring-blue-500');
    }
  }
 
  // ============================================
  // PRICING CALCULATION
  // ============================================
  function getPriceForQuantity(qty) {
    if (!state.selectedPatchType) return 0;
 
    // Leather uses per-size pricing — look up directly from the matrix.
    if (state.selectedPatchType === 'leather') {
      const pricingSize = state.leatherPricingSize;
      if (!pricingSize) return 0;
 
      // Find tier index for this quantity
      let tierIndex = 0;
      for (let i = LEATHER_TIER_DEFS.length - 1; i >= 0; i--) {
        if (qty >= LEATHER_TIER_DEFS[i].minQty) {
          tierIndex = LEATHER_TIER_DEFS[i].index;
          break;
        }
      }
      const cents = getLeatherPriceCents(pricingSize, tierIndex);
      return cents || 0;
    }
 
    // All other patch types: use the standard tier table
    const pricing = getPricingForType(state.selectedPatchType);
    const tiers = pricing.tiers;
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) {
        return tiers[i].priceCents;
      }
    }
    return tiers[0]?.priceCents || 0;
  }
 
  function getTierForQuantity(qty) {
    if (!state.selectedPatchType) return null;
 
    const pricing = getPricingForType(state.selectedPatchType);
    const tiers = pricing.tiers;
 
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) {
        return tiers[i];
      }
    }
 
    return tiers[0];
  }
 
  function calculateTotal() {
    const priceCents = getPriceForQuantity(state.selectedQuantity);
    const subtotalCents = priceCents * state.selectedQuantity;
 
    let moldFeeCents = 0;
    if (state.selectedPatchType === 'rubber' && state.isRubberReorder === false) {
      moldFeeCents = state.rubberMoldFeeCents;
    }
 
    // Size surcharge — does NOT apply to leather (leather price is size-based)
    let sizeSurchargeCents = 0;
    if (state.sizeSurchargeApplies && state.currentSizeSurchargeCents > 0
        && patchTypeUsesSizeSurcharge(state.selectedPatchType)) {
      sizeSurchargeCents = state.currentSizeSurchargeCents * state.selectedQuantity;
    }
 
    const totalCents = subtotalCents + moldFeeCents + sizeSurchargeCents;
 
    return {
      pricePerPatch: priceCents / 100,
      quantity: state.selectedQuantity,
      subtotal: subtotalCents / 100,
      moldFee: moldFeeCents / 100,
      sizeSurcharge: sizeSurchargeCents / 100,
      sizeSurchargePerPatch: state.currentSizeSurchargeCents / 100,
      total: totalCents / 100
    };
  }
 
  function updatePricing() {
    const pricing = calculateTotal();
 
    const pricePerPatch = document.querySelector('[data-price-per-patch]');
    const summaryQuantity = document.querySelector('[data-summary-quantity]');
    const patchSubtotal = document.querySelector('[data-patch-subtotal]');
    const moldFeeLine = document.querySelector('[data-mold-fee-line]');
    const moldFeeAmount = document.querySelector('[data-mold-fee-amount]');
    const sizeSurchargeLine = document.querySelector('[data-size-surcharge-line]');
    const sizeSurchargeAmount = document.querySelector('[data-size-surcharge-amount]');
    const orderTotal = document.querySelector('[data-order-total]');
 
    if (pricePerPatch) pricePerPatch.textContent = `$${pricing.pricePerPatch.toFixed(2)}`;
    if (summaryQuantity) summaryQuantity.textContent = pricing.quantity;
    if (patchSubtotal) patchSubtotal.textContent = `$${pricing.subtotal.toFixed(2)}`;
    if (orderTotal) orderTotal.textContent = `$${pricing.total.toFixed(2)}`;
 
    if (moldFeeLine) {
      if (state.selectedPatchType === 'rubber' && state.isRubberReorder === false) {
        moldFeeLine.style.display = 'flex';
        if (moldFeeAmount) moldFeeAmount.textContent = `+$${pricing.moldFee.toFixed(2)}`;
      } else {
        moldFeeLine.style.display = 'none';
      }
    }
 
    if (sizeSurchargeLine) {
      // Hide the surcharge summary line for leather even if there's stale state
      if (state.sizeSurchargeApplies && patchTypeUsesSizeSurcharge(state.selectedPatchType)) {
        sizeSurchargeLine.style.display = 'flex';
        if (sizeSurchargeAmount) {
          sizeSurchargeAmount.textContent = `+$${pricing.sizeSurcharge.toFixed(2)} ($${pricing.sizeSurchargePerPatch.toFixed(2)}/ea)`;
        }
      } else {
        sizeSurchargeLine.style.display = 'none';
      }
    }
  }
 
  // ============================================
  // LOGO UPLOAD
  // ============================================
  function setupLogoUpload() {
    const uploadInput = document.querySelector('[data-logo-upload]');
    const logoLabel = document.querySelector('[data-logo-label]');
 
    if (!uploadInput) return;
 
    uploadInput.addEventListener('change', function(e) {
      const file = e.target.files[0];
      if (!file) return;
 
      const allowedExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.pdf', '.eps', '.ai'];
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
 
      if (!allowedExtensions.includes(fileExtension)) {
        showNotification('Invalid file type. Please upload PNG, JPG, GIF, SVG, PDF, EPS, or AI files.', 'error');
        e.target.value = '';
        return;
      }
 
      const maxSizeMB = 10;
      if (file.size > maxSizeMB * 1024 * 1024) {
        showNotification(`File too large. Maximum size is ${maxSizeMB}MB.`, 'error');
        e.target.value = '';
        return;
      }
 
      state.logoFile = file;
 
      const reader = new FileReader();
      reader.onload = function(e) {
        state.logoBase64 = e.target.result;
        updateLogoUI(file, logoLabel);
        updateUI();
        showNotification('Logo uploaded successfully!', 'success');
        scrollToSection('[data-price-section]');
      };
      reader.onerror = function() {
        showNotification('Error reading file. Please try again.', 'error');
      };
      reader.readAsDataURL(file);
    });
  }
 
  function updateLogoUI(file, labelElement) {
    if (!labelElement) return;
 
    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.substring(fileName.lastIndexOf('.')).toUpperCase();
    const isImage = file.type.startsWith('image/') && !fileName.endsWith('.eps');
 
    if (isImage && state.logoBase64) {
      labelElement.innerHTML = `
        <div class="flex items-center space-x-4">
          <img src="${state.logoBase64}" alt="Logo preview" class="w-16 h-16 object-contain border border-gray-300 rounded">
          <div class="text-left">
            <p class="text-lg font-medium text-green-600">${file.name}</p>
            <p class="text-sm text-muted-foreground">${(file.size / 1024).toFixed(1)} KB - ${fileExtension}</p>
          </div>
        </div>
      `;
    } else {
      labelElement.innerHTML = `
        <div class="flex items-center space-x-4">
          <div class="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-300 rounded-lg">
            <svg class="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
          </div>
          <div class="text-left">
            <p class="text-lg font-medium text-green-600">${file.name}</p>
            <p class="text-sm text-muted-foreground">${(file.size / 1024).toFixed(1)} KB - ${fileExtension}</p>
          </div>
        </div>
      `;
    }
  }
 
  // ============================================
  // ORDER BUTTON & CHECKOUT
  // ============================================
  function setupOrderButton() {
    const finishBtn = document.querySelector('[data-finish-order]');
    if (finishBtn) {
      finishBtn.addEventListener('click', handleFinishOrder);
    }
  }
 
  function isOrderComplete() {
    if (!state.selectedPatchType) return false;
    if (!state.selectedBackingType) return false;
    if (!state.logoFile) return false;
 
    if (!state.patchWidth || !state.patchHeight || !state.squareInches) return false;
 
    switch (state.selectedPatchType) {
      case 'leather':
      case 'fabric':
        if (!state.patchShape || !state.patchMaterial) return false;
        break;
      case 'embroidery':
        break;
      case 'rubber':
        if (state.isRubberReorder === null) return false;
        if (state.selectedQuantity < getMinQuantityForType('rubber')) return false;
        break;
    }
 
    return true;
  }
 
  async function handleFinishOrder() {
    console.log('=== PATCH ORDER CHECKOUT ===');
 
    if (!isOrderComplete()) {
      showNotification('Please complete all steps before proceeding.', 'error');
      return;
    }
 
    const finishBtn = document.querySelector('[data-finish-order]');
    const originalText = finishBtn.innerHTML;
    finishBtn.disabled = true;
    finishBtn.innerHTML = 'Processing...';
 
    try {
      const cartItems = [];
      const pricing = calculateTotal();
 
      const properties = {
        'Patch Type': capitalizeFirst(state.selectedPatchType),
        'Backing Type': state.selectedBackingType === 'heat-activated' ? 'Heat Activated Adhesive' : 'Standard Backing (Sew-on)',
        'Dimensions': `${state.patchWidth}" x ${state.patchHeight}"`,
        'Square Inches': state.squareInches.toFixed(2)
      };
 
      // For leather, also record the pricing-size used (helps fulfillment)
      if (state.selectedPatchType === 'leather' && state.leatherPricingSize) {
        properties['Pricing Size'] = `${state.leatherPricingSize}"`;
      }
 
      const commentsField = document.querySelector('[data-customer-comments]');
      const customerComments = commentsField ? commentsField.value.trim() : '';
      if (customerComments) {
        properties['Specific Requests'] = customerComments;
      }
 
      switch (state.selectedPatchType) {
        case 'leather':
        case 'fabric':
          properties['Shape'] = capitalizeFirst(state.patchShape);
          if (state.patchMaterialTitle) {
            properties['Material'] = state.patchMaterialTitle;
          }
          break;
        case 'embroidery':
          break;
        case 'rubber':
          properties['Is Reorder'] = state.isRubberReorder ? 'Yes' : 'No';
          break;
      }
 
      const form = document.querySelector('[data-patch-form]');
 
      if (state.selectedVariantId) {
        cartItems.push({
          id: parseInt(state.selectedVariantId, 10),
          quantity: state.selectedQuantity,
          properties: properties
        });
      }
 
      if (state.selectedPatchType === 'rubber' && state.isRubberReorder === false) {
        const moldFeeVariantId = form?.dataset.moldFeeVariantId;
 
        if (moldFeeVariantId) {
          cartItems.push({
            id: parseInt(moldFeeVariantId, 10),
            quantity: 1,
            properties: {
              '_mold_fee': 'true',
              'For': 'Rubber Patch Mold (New Design)'
            }
          });
        }
      }
 
      // Size surcharge — non-leather only
      console.log('Size surcharge debug:', {
        sizeSurchargeApplies: state.sizeSurchargeApplies,
        currentSizeSurchargeCents: state.currentSizeSurchargeCents,
        squareInches: state.squareInches,
        patchType: state.selectedPatchType,
        usesSurcharge: patchTypeUsesSizeSurcharge(state.selectedPatchType),
        variantId: form?.dataset.sizeSurchargeVariantId
      });
 
      if (state.sizeSurchargeApplies
          && state.currentSizeSurchargeCents > 0
          && patchTypeUsesSizeSurcharge(state.selectedPatchType)) {
        const sizeSurchargeVariantId = form?.dataset.sizeSurchargeVariantId;
 
        if (sizeSurchargeVariantId) {
          const tierDollars = state.currentSizeSurchargeCents / 100;
          const surchargeQuantity = tierDollars * state.selectedQuantity;
 
          cartItems.push({
            id: parseInt(sizeSurchargeVariantId, 10),
            quantity: surchargeQuantity,
            properties: {
              '_size_surcharge': 'true',
              'For': `Large Patch Surcharge (${state.squareInches.toFixed(2)} sq in @ $${tierDollars}/patch)`
            }
          });
        }
      }
 
      console.log('Cart items:', cartItems);
 
      if (cartItems.length > 0) {
        finishBtn.innerHTML = 'Adding to cart...';
 
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: cartItems })
        });
 
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add to cart');
        }
      }
 
      finishBtn.innerHTML = 'Uploading logo...';
      const uploadResult = await uploadLogoToCloudinary();
 
      if (!uploadResult.success) {
        throw new Error(`Logo upload failed: ${uploadResult.error}`);
      }
 
      finishBtn.innerHTML = 'Finalizing order...';
 
      let cartNote = `Patch Type: ${capitalizeFirst(state.selectedPatchType)}
Backing: ${state.selectedBackingType === 'heat-activated' ? 'Heat Activated Adhesive' : 'Standard Backing (Sew-on)'}`;
 
      switch (state.selectedPatchType) {
        case 'leather':
        case 'fabric':
          cartNote += `\nShape: ${capitalizeFirst(state.patchShape)}`;
          if (state.patchMaterialTitle) {
            cartNote += `\nMaterial: ${state.patchMaterialTitle}`;
          }
          break;
        case 'embroidery':
          break;
        case 'rubber':
          cartNote += `\nIs Reorder: ${state.isRubberReorder ? 'Yes' : 'No'}`;
          break;
      }
 
      cartNote += `\nLogo: ${state.logoFile.name}`;
 
      if (customerComments) {
        cartNote += `\nSpecific Requests: ${customerComments}`;
      }
 
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          note: cartNote.trim(),
          attributes: {
            'Logo_Filename': state.logoFile.name,
            'Logo_URL': uploadResult.url,
            'Logo_Cloudinary_ID': uploadResult.publicId,
            'Patch_Type': state.selectedPatchType,
            'Backing_Type': state.selectedBackingType
          }
        })
      });
 
      window.location.href = '/checkout';
 
    } catch (error) {
      console.error('Order error:', error);
      showNotification(`Error: ${error.message}`, 'error');
      finishBtn.disabled = false;
      finishBtn.innerHTML = originalText;
    }
  }
 
  // ============================================
  // CLOUDINARY UPLOAD
  // ============================================
  async function uploadLogoToCloudinary() {
    const CLOUD_NAME = 'dsvcreexw';
    const UPLOAD_PRESET = 'sixhats_custom_logo';
 
    try {
      const formData = new FormData();
      formData.append('file', state.logoBase64);
      formData.append('upload_preset', UPLOAD_PRESET);
      formData.append('folder', 'patch-orders');
 
      const timestamp = new Date().toISOString();
      formData.append('context', `original_filename=${state.logoFile.name}|upload_date=${timestamp}`);
      formData.append('tags', 'patch-order,logo,custom-order');
 
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/upload`,
        { method: 'POST', body: formData }
      );
 
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }
 
      const result = await response.json();
 
      return {
        success: true,
        url: result.secure_url,
        publicId: result.public_id
      };
 
    } catch (error) {
      console.error('Cloudinary upload error:', error);
      return { success: false, error: error.message };
    }
  }
 
  // ============================================
  // UI HELPERS
  // ============================================
  function showSection(selector) {
    const section = document.querySelector(selector);
    if (section) section.style.display = 'block';
  }
 
  function hideSection(selector) {
    const section = document.querySelector(selector);
    if (section) section.style.display = 'none';
  }
 
  // Smoothly scroll a section into view. Used to advance the user to the
  // next step automatically after they make a choice. Small delay so any
  // show/transition animations complete before we scroll.
  function scrollToSection(selector) {
    const section = document.querySelector(selector);
    if (!section) return;
    setTimeout(() => {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }
 
  function capitalizeFirst(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
 
  function updateUI() {
    updateStatusIndicators();
    updateOrderButton();
  }
 
  function updateStatusIndicators() {
    updateStatusIndicator('patch', !!state.selectedPatchType && !!state.selectedBackingType);
 
    let configComplete = false;
    const hasDimensions = state.patchWidth > 0 && state.patchHeight > 0;
    if (state.selectedPatchType) {
      switch (state.selectedPatchType) {
        case 'leather':
        case 'fabric':
          configComplete = !!(state.patchShape && state.patchMaterial && hasDimensions);
          break;
        case 'embroidery':
          configComplete = hasDimensions;
          break;
        case 'rubber':
          configComplete = state.isRubberReorder !== null && hasDimensions;
          break;
      }
    }
    updateStatusIndicator('config', configComplete && state.selectedQuantity > 0);
 
    updateStatusIndicator('logo', !!state.logoFile);
  }
 
  function updateStatusIndicator(type, isComplete) {
    const indicator = document.querySelector(`[data-status-indicator="${type}"]`);
    const label = document.querySelector(`[data-status-label="${type}"]`);
 
    if (!indicator || !label) return;
 
    if (isComplete) {
      indicator.classList.remove('bg-gray-300', 'text-gray-600');
      indicator.classList.add('text-white');
      indicator.style.backgroundColor = '#22c55e';
      indicator.innerHTML = '<svg style="width: 14px; height: 14px;" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>';
      label.classList.remove('text-gray-600');
      label.style.color = '#16a34a';
      label.style.fontWeight = '500';
    } else {
      indicator.classList.remove('text-white');
      indicator.classList.add('bg-gray-300', 'text-gray-600');
      indicator.style.backgroundColor = '';
      const number = type === 'patch' ? '1' : type === 'config' ? '2' : '3';
      indicator.textContent = number;
      label.classList.add('text-gray-600');
      label.style.color = '';
      label.style.fontWeight = '';
    }
  }
 
  function updateOrderButton() {
    const finishBtn = document.querySelector('[data-finish-order]');
    const missingWarning = document.querySelector('[data-missing-warning]');
 
    if (!finishBtn) return;
 
    const complete = isOrderComplete();
    finishBtn.disabled = !complete;
 
    if (missingWarning) {
      missingWarning.style.display = complete ? 'none' : 'block';
    }
  }
 
  // ============================================
  // NOTIFICATION SYSTEM
  // ============================================
  function setupNotificationSystem() {
    if (!document.getElementById('patch-notification-container')) {
      const container = document.createElement('div');
      container.id = 'patch-notification-container';
      container.className = 'fixed top-4 right-4 space-y-2';
      container.style.zIndex = '9999';
      document.body.appendChild(container);
    }
  }
 
  function showNotification(message, type = 'error') {
    const container = document.getElementById('patch-notification-container');
    if (!container) return;
 
    const notification = document.createElement('div');
    const bgColor = type === 'error' ? 'bg-red-500' : type === 'success' ? 'bg-green-500' : 'bg-blue-500';
 
    notification.className = `${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 max-w-md`;
    notification.innerHTML = `
      <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        ${type === 'error'
          ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>'
          : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>'}
      </svg>
      <span class="flex-1">${message}</span>
      <button class="text-white hover:text-gray-200 transition-colors" onclick="this.parentElement.remove()">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    `;
 
    container.appendChild(notification);
 
    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transform = 'translateX(100%)';
      setTimeout(() => notification.remove(), 300);
    }, 5000);
  }
 
  // ============================================
  // INITIALIZE ON DOM READY
  // ============================================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
 
})();